import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { getCurrentOrgId } from '@thoughtspot/session-service';
import { getErrorObjectFromGraphQLErrors } from "../../../utils/error-handling/error-utils";
import { convertSettingsToTemplateProperties, convertTemplatePropertiesToSettings, useCreateEmailCustomizationMutation, useDeleteOrgEmailCustomizationMutation, useSearchEmailCustomizationMutation, useUpdateEmailCustomizationMutation, } from '../../../services/email-customization/email-customization-service';
import { EMAIL_CUSTOMIZATION_CONSTANTS, TranslationKeys } from './constants';
import { DEFAULT_SETTINGS, trackEmailCustomisationOpened, } from './email-customization-analytics';
import { EmailCustomizationWizardContainer } from './email-customization-wizard.container';
import { EmailPreview } from './email-preview.component';
import styles from './email-customizations.module.scss';
export const EmailCustomizations = ({ currentOrgId: propCurrentOrgId, }) => {
    const { t } = useTranslation();
    const [currentSampleEmail, setCurrentSampleEmail] = useState('test-email');
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [showToastCheckmark, setShowToastCheckmark] = useState(true);
    const propOrgId = propCurrentOrgId !== undefined
        ? propCurrentOrgId.toString()
        : getCurrentOrgId().toString();
    const currentOrgIdtmp = getCurrentOrgId().toString();
    const [currentOrgId, setCurrentOrgId] = useState(() => {
        return currentOrgIdtmp === '0' ? propOrgId : currentOrgIdtmp;
    });
    const [systemDefaultSettings, setSystemDefaultSettings] = useState(null);
    const [organizationSettings, setOrganizationSettings] = useState(null);
    useEffect(() => {
        const newCurrentOrgId = currentOrgIdtmp === '0' ? propOrgId : currentOrgIdtmp;
        setCurrentOrgId(newCurrentOrgId);
    }, [propOrgId, currentOrgIdtmp]);
    const { searchEmailCustomization, data, loading, } = useSearchEmailCustomizationMutation();
    const { searchEmailCustomization: searchEmailCustomizationDefault, data: dataDefault, loading: loadingDefault, } = useSearchEmailCustomizationMutation();
    const { updateEmailCustomization, loading: loadingUpdate, data: dataUpdate, error: errorUpdate, } = useUpdateEmailCustomizationMutation();
    const { deleteOrgEmailCustomization, data: dataDelete, loading: loadingDelete, error: errorDelete, } = useDeleteOrgEmailCustomizationMutation();
    const { createEmailCustomization, loading: loadingCreate, data: dataCreate, error: errorCreate, } = useCreateEmailCustomizationMutation();
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [pageOpenTime] = useState(() => Date.now());
    const allOrgsSettings = useMemo(() => {
        if (systemDefaultSettings) {
            return convertTemplatePropertiesToSettings(systemDefaultSettings);
        }
        return undefined;
    }, [systemDefaultSettings]);
    const loadEmailCustomizations = async () => {
        setIsDataLoaded(false);
        if (currentOrgId !== undefined && currentOrgId !== null) {
            searchEmailCustomizationDefault(['-1']);
            if (currentOrgId !== '-1') {
                searchEmailCustomization([currentOrgId]);
            }
        }
        else {
            setIsDataLoaded(true);
        }
    };
    useEffect(() => {
        loadEmailCustomizations();
    }, [currentOrgId]);
    useEffect(() => {
        if (data !== undefined) {
            if (data?.data && data.data.length > 0) {
                const apiSettings = data.data[0];
                setOrganizationSettings(apiSettings.template_properties);
                setIsDataLoaded(true);
            }
            else {
                setOrganizationSettings(null);
                setIsDataLoaded(true);
            }
        }
    }, [data]);
    useEffect(() => {
        if (dataDefault !== undefined) {
            if (dataDefault?.data && dataDefault.data.length > 0) {
                const apiSettings = dataDefault.data[0];
                setSystemDefaultSettings(apiSettings.template_properties);
                setIsDataLoaded(true);
            }
            else {
                setSystemDefaultSettings(null);
                setIsDataLoaded(true);
            }
        }
    }, [dataDefault]);
    useEffect(() => {
        if (systemDefaultSettings && organizationSettings) {
            const mergedData = {
                ...systemDefaultSettings,
                ...organizationSettings,
            };
            setSettings(convertTemplatePropertiesToSettings(mergedData));
        }
        else if (systemDefaultSettings) {
            setSettings(convertTemplatePropertiesToSettings(systemDefaultSettings));
        }
        else if (organizationSettings) {
            setSettings(convertTemplatePropertiesToSettings(organizationSettings));
        }
    }, [organizationSettings, systemDefaultSettings]);
    useEffect(() => {
        if (isDataLoaded) {
            const hasCustomisation = organizationSettings !== null;
            const customisationID = hasCustomisation ? currentOrgId : 0;
            const renderTime = Date.now() - pageOpenTime;
            trackEmailCustomisationOpened(hasCustomisation, customisationID, renderTime);
        }
    }, [isDataLoaded, organizationSettings, currentOrgId, pageOpenTime]);
    const sampleEmails = useMemo(() => [
        {
            id: 'test-email',
            name: t(TranslationKeys.TEST_EMAIL_NAME),
            description: t(TranslationKeys.SAMPLE_EMAIL_DESCRIPTION),
        },
    ], [t]);
    const isDefaultValue = (key, value) => {
        return DEFAULT_SETTINGS[key] === value;
    };
    const renderSettingsItem = (label, value, colorPreview, showDefault = true, errorMessage) => (React.createElement("div", { className: styles.settingsItem },
        React.createElement("div", { className: styles.settingsLabel }, label),
        React.createElement("div", { className: styles.settingsValue },
            colorPreview && (React.createElement("div", { className: styles.colorValue },
                React.createElement("div", { className: styles.colorPreview, style: { backgroundColor: colorPreview } }),
                React.createElement("div", { className: styles.colorText }, value))),
            !colorPreview && (React.createElement("div", { className: styles.valueText }, value)),
            showDefault && (React.createElement("div", { className: styles.defaultText }, t(TranslationKeys.DEFAULT_INDICATOR))),
            errorMessage && (React.createElement("div", { className: styles.errorText }, errorMessage)))));
    const currentEmailInfo = sampleEmails.find(email => email.id === currentSampleEmail);
    const handleWizardSave = useCallback(async (newSettings) => {
        if (newSettings.isResetToDefault) {
            await deleteOrgEmailCustomization([
                currentOrgId?.toString() || '-1',
            ]);
            setSettings(DEFAULT_SETTINGS);
        }
        else {
            const cleanSettings = { ...newSettings };
            const hasExistingSettings = currentOrgId === '-1'
                ? systemDefaultSettings !== null
                : organizationSettings !== null;
            let baselineSettings;
            if (hasExistingSettings) {
                if (currentOrgId === '-1') {
                    baselineSettings = convertTemplatePropertiesToSettings(systemDefaultSettings);
                }
                else if (systemDefaultSettings && organizationSettings) {
                    const mergedData = {
                        ...systemDefaultSettings,
                        ...organizationSettings,
                    };
                    baselineSettings = convertTemplatePropertiesToSettings(mergedData);
                }
                else if (organizationSettings) {
                    baselineSettings = convertTemplatePropertiesToSettings(organizationSettings);
                }
                else {
                    baselineSettings = DEFAULT_SETTINGS;
                }
            }
            else if (currentOrgId === '-1') {
                baselineSettings = DEFAULT_SETTINGS;
            }
            else {
                baselineSettings = systemDefaultSettings
                    ? convertTemplatePropertiesToSettings(systemDefaultSettings)
                    : DEFAULT_SETTINGS;
            }
            const templateProperties = convertSettingsToTemplateProperties(cleanSettings, baselineSettings);
            if (Object.keys(templateProperties).length === 0) {
                setShowToastCheckmark(false);
                setToastMessage(t(TranslationKeys.TOAST_NO_CHANGES));
                setIsToastVisible(true);
                return;
            }
            if (hasExistingSettings) {
                await updateEmailCustomization(templateProperties, currentOrgId?.toString() || '-1');
            }
            else {
                await createEmailCustomization(templateProperties, currentOrgId?.toString() || '-1');
            }
        }
    }, [
        deleteOrgEmailCustomization,
        createEmailCustomization,
        updateEmailCustomization,
        currentOrgId,
        organizationSettings,
        systemDefaultSettings,
    ]);
    useEffect(() => {
        if (dataDelete && !loadingDelete && !errorDelete) {
            loadEmailCustomizations();
            setShowToastCheckmark(true);
            setToastMessage(t(TranslationKeys.TOAST_RESET_SUCCESS));
            setIsToastVisible(true);
            setIsWizardOpen(false);
        }
    }, [dataDelete, loadingDelete, errorDelete]);
    useEffect(() => {
        if (dataCreate && !loadingCreate && !errorCreate) {
            setShowToastCheckmark(true);
            setToastMessage(t(TranslationKeys.TOAST_CREATE_SUCCESS));
            setIsToastVisible(true);
            setIsWizardOpen(false);
            loadEmailCustomizations();
        }
    }, [dataCreate, loadingCreate, errorCreate]);
    useEffect(() => {
        if (dataUpdate && !loadingUpdate && !errorUpdate) {
            setShowToastCheckmark(true);
            setToastMessage(t(TranslationKeys.TOAST_UPDATE_SUCCESS));
            setIsToastVisible(true);
            setIsWizardOpen(false);
            loadEmailCustomizations();
        }
    }, [dataUpdate, loadingUpdate, errorUpdate]);
    const getPrismErrorMessage = (apolloError) => {
        const errorObjects = getErrorObjectFromGraphQLErrors(apolloError);
        if (errorObjects.length > 0) {
            const errorMessage = errorObjects[0]?.errorObj?.extensions?.exception
                ?.upstreamResponse?.data?.error?.message?.debug;
            return errorMessage;
        }
        return t(TranslationKeys.TOAST_SAVE_FAILED);
    };
    useEffect(() => {
        if (errorDelete) {
            const errorMessage = getPrismErrorMessage(errorDelete);
            setShowToastCheckmark(false);
            setToastMessage(errorMessage);
            setIsToastVisible(true);
        }
    }, [errorDelete]);
    useEffect(() => {
        if (errorCreate) {
            const errorMessage = getPrismErrorMessage(errorCreate);
            setShowToastCheckmark(false);
            setToastMessage(errorMessage);
            setIsToastVisible(true);
        }
    }, [errorCreate]);
    useEffect(() => {
        if (errorUpdate) {
            const errorMessage = getPrismErrorMessage(errorUpdate);
            setShowToastCheckmark(false);
            setToastMessage(errorMessage);
            setIsToastVisible(true);
        }
    }, [errorUpdate]);
    return (React.createElement("div", { className: styles.emailCustomizationViewContainer },
        React.createElement("div", { className: styles.customHeader },
            React.createElement("div", { className: styles.headerContent },
                React.createElement("div", { className: styles.headerTitle }, t(TranslationKeys.TITLE)),
                React.createElement("div", { className: styles.headerSubtitle }, t(TranslationKeys.SUBTITLE))),
            React.createElement(Button, { type: "primary", text: t(TranslationKeys.EDIT_BUTTON), onClick: () => setIsWizardOpen(true), className: styles.editButton, isDisabled: loading })),
        React.createElement(Divider, null),
        React.createElement(Horizontal, { className: styles.mainContent },
            React.createElement("div", { className: styles.leftPanel },
                React.createElement("div", { className: styles.settingsSection },
                    React.createElement("div", { className: styles.sectionTitle }, t(TranslationKeys.CURRENT_SETTINGS)),
                    React.createElement("div", { className: styles.settingsGroup },
                        React.createElement("div", { className: styles.groupHeader },
                            React.createElement("div", { className: styles.groupTitle }, t(TranslationKeys.LOGO_SECTION_TITLE))),
                        React.createElement("div", { className: styles.groupContent }, renderSettingsItem(t(TranslationKeys.LOGO_LABEL), settings.hideLogoUrl ===
                            EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN
                            ? t(TranslationKeys.LOGO_SAME_AS_STYLING)
                            : t(TranslationKeys.HIDDEN), undefined, settings.hideLogoUrl ===
                            EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN
                            ? settings.logo ===
                                EMAIL_CUSTOMIZATION_CONSTANTS.SAME_AS_STYLING
                            : false))),
                    React.createElement("div", { className: styles.settingsGroup },
                        React.createElement("div", { className: styles.groupHeader },
                            React.createElement("div", { className: styles.groupTitle }, t(TranslationKeys.COLOR_SECTION_TITLE))),
                        React.createElement("div", { className: styles.groupContent },
                            renderSettingsItem(t(TranslationKeys.BUTTON_COLOR_LABEL), settings.buttonColor, settings.buttonColor, isDefaultValue('buttonColor', settings.buttonColor)),
                            renderSettingsItem(t(TranslationKeys.BUTTON_TEXT_COLOR_LABEL), settings.primaryTextColor, settings.primaryTextColor, isDefaultValue('primaryTextColor', settings.primaryTextColor)),
                            renderSettingsItem(t(TranslationKeys.BACKGROUND_COLOR), settings.cardBackgroundColor, settings.cardBackgroundColor, isDefaultValue('cardBackgroundColor', settings.cardBackgroundColor)))),
                    React.createElement("div", { className: styles.settingsGroup },
                        React.createElement("div", { className: styles.groupHeader },
                            React.createElement("div", { className: styles.groupTitle }, t(TranslationKeys.FOOTER_SECTION_TITLE))),
                        React.createElement("div", { className: styles.groupContent },
                            renderSettingsItem(t(TranslationKeys.PRODUCT_NAME_LABEL), settings.hideProductName, undefined, isDefaultValue('hideProductName', settings.hideProductName)),
                            renderSettingsItem(t(TranslationKeys.PHONE_NUMBER_LABEL), settings.hideFooterPhone, undefined, isDefaultValue('hideFooterPhone', settings.hideFooterPhone)),
                            renderSettingsItem(t(TranslationKeys.ADDRESS_LABEL), settings.hideFooterAddress, undefined, isDefaultValue('hideFooterAddress', settings.hideFooterAddress)),
                            renderSettingsItem(t(TranslationKeys.MOBILE_APP_NUDGE_LABEL), settings.mobileAppNudge, undefined, isDefaultValue('mobileAppNudge', settings.mobileAppNudge)),
                            renderSettingsItem(t(TranslationKeys.MODIFY_ALERT_LABEL), settings.hideModifyAlert, undefined, isDefaultValue('hideModifyAlert', settings.hideModifyAlert)),
                            renderSettingsItem(t(TranslationKeys.UNSUBSCRIBE_LINK_LABEL), settings.hideUnsubscribeLink, undefined, isDefaultValue('hideUnsubscribeLink', settings.hideUnsubscribeLink)),
                            renderSettingsItem(t(TranslationKeys.ERROR_MESSAGE_LABEL), settings.hideErrorMessage, undefined, isDefaultValue('hideErrorMessage', settings.hideErrorMessage)),
                            settings.hideFooterAddress ===
                                EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN && (React.createElement("div", { className: styles.settingsItem },
                                React.createElement("div", { className: styles.settingsLabel }, t(TranslationKeys.COMPANY_SIGNATURE_LABEL)),
                                React.createElement("div", { className: styles.signatureContent },
                                    React.createElement("div", { className: styles.signatureText }, settings.footerAddress
                                        .split('\n')
                                        .map((line, index) => (React.createElement("span", null,
                                        line,
                                        index <
                                            settings.footerAddress.split('\n').length -
                                                1 && React.createElement("br", null)))))))),
                            settings.hidePrivacyPolicy ===
                                EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN &&
                                renderSettingsItem(t(TranslationKeys.PRIVACY_POLICY), settings.webLinkAddress1, undefined, false),
                            settings.hideContactSupportUrl ===
                                EMAIL_CUSTOMIZATION_CONSTANTS.VISIBILITY_SHOWN &&
                                renderSettingsItem(t(TranslationKeys.CONTACT_SUPPORT_LINK), settings.contactSupportUrl, undefined, false),
                            renderSettingsItem(t(TranslationKeys.MANAGE_NOTIFICATION_LABEL), settings.hideManageNotification, undefined, isDefaultValue('hideManageNotification', settings.hideManageNotification)),
                            renderSettingsItem('Company Website URL', settings.companyWebsiteUrl, undefined, isDefaultValue('companyWebsiteUrl', settings.companyWebsiteUrl)))))),
            React.createElement(Divider, { vertical: true }),
            React.createElement("div", { className: styles.rightPanel },
                React.createElement("div", { className: styles.previewContainer },
                    React.createElement("div", { className: styles.previewHeader },
                        React.createElement("div", { className: styles.emailInfo },
                            React.createElement("div", { className: styles.emailDescription }, currentEmailInfo?.description),
                            React.createElement("div", { className: styles.emailName }, currentEmailInfo?.name))),
                    React.createElement("div", { className: styles.emailPreviewWrapper }, isDataLoaded ? (React.createElement(EmailPreview, { settings: settings })) : (React.createElement("div", { className: styles.loadingContainer }, t(TranslationKeys.LOADING_PREVIEW))))))),
        React.createElement(GlobalAlert.Toast, { isVisible: isToastVisible, message: toastMessage, onClose: () => setIsToastVisible(false), autoHide: true, showCheckmarkIcon: showToastCheckmark }),
        React.createElement(EmailCustomizationWizardContainer, { isOpen: isWizardOpen, onClose: () => setIsWizardOpen(false), onSave: handleWizardSave, initialSettings: settings, orgId: currentOrgId, allOrgsSettings: allOrgsSettings })));
};
export default EmailCustomizations;
//# sourceMappingURL=email-customizations.container.js.map